/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0; you may not use this file except in compliance with the Elastic License
 * 2.0.
 */

// DO NOT MODIFY THIS FILE BY HAND. IT IS GENERATED BY
// `yarn workspace @elastic/esql-definitions generate` FROM
// `elasticsearch/esql/x-pack-esql/definition/functions/date_diff.json`.

import type { FunctionDefinition } from '../../definition_types';

/**
 * Subtracts the `startTimestamp` from the `endTimestamp` and returns the difference in multiples of `unit`.
 * If `startTimestamp` is later than the `endTimestamp`, negative values are returned.
 *
 * @example
 * ROW date1 = TO_DATETIME("2023-12-02T11:00:00.000Z"),
 *     date2 = TO_DATETIME("2023-12-02T11:00:00.001Z")
 * | EVAL dd_ms = DATE_DIFF("microseconds", date1, date2)
 *
 * @example
 * ROW end_23 = TO_DATETIME("2023-12-31T23:59:59.999Z"),
 *   start_24 = TO_DATETIME("2024-01-01T00:00:00.000Z"),
 *     end_24 = TO_DATETIME("2024-12-31T23:59:59.999")
 * | EVAL end23_to_start24 = DATE_DIFF("year", end_23, start_24)
 * | EVAL end23_to_end24   = DATE_DIFF("year", end_23, end_24)
 * | EVAL start_to_end_24  = DATE_DIFF("year", start_24, end_24)
 */
const definition: FunctionDefinition = {
  type: 'scalar',
  name: 'date_diff',
  signatures: [
    {
      params: [
        {
          name: 'unit',
          type: 'keyword',
          optional: false,
        },
        {
          name: 'startTimestamp',
          type: 'date',
          optional: false,
        },
        {
          name: 'endTimestamp',
          type: 'date',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'integer',
    },
    {
      params: [
        {
          name: 'unit',
          type: 'keyword',
          optional: false,
        },
        {
          name: 'startTimestamp',
          type: 'date',
          optional: false,
        },
        {
          name: 'endTimestamp',
          type: 'date_nanos',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'integer',
    },
    {
      params: [
        {
          name: 'unit',
          type: 'keyword',
          optional: false,
        },
        {
          name: 'startTimestamp',
          type: 'date_nanos',
          optional: false,
        },
        {
          name: 'endTimestamp',
          type: 'date',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'integer',
    },
    {
      params: [
        {
          name: 'unit',
          type: 'keyword',
          optional: false,
        },
        {
          name: 'startTimestamp',
          type: 'date_nanos',
          optional: false,
        },
        {
          name: 'endTimestamp',
          type: 'date_nanos',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'integer',
    },
    {
      params: [
        {
          name: 'unit',
          type: 'text',
          optional: false,
        },
        {
          name: 'startTimestamp',
          type: 'date',
          optional: false,
        },
        {
          name: 'endTimestamp',
          type: 'date',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'integer',
    },
    {
      params: [
        {
          name: 'unit',
          type: 'text',
          optional: false,
        },
        {
          name: 'startTimestamp',
          type: 'date',
          optional: false,
        },
        {
          name: 'endTimestamp',
          type: 'date_nanos',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'integer',
    },
    {
      params: [
        {
          name: 'unit',
          type: 'text',
          optional: false,
        },
        {
          name: 'startTimestamp',
          type: 'date_nanos',
          optional: false,
        },
        {
          name: 'endTimestamp',
          type: 'date',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'integer',
    },
    {
      params: [
        {
          name: 'unit',
          type: 'text',
          optional: false,
        },
        {
          name: 'startTimestamp',
          type: 'date_nanos',
          optional: false,
        },
        {
          name: 'endTimestamp',
          type: 'date_nanos',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'integer',
    },
  ],
  preview: false,
  snapshotOnly: false,
};

export default definition;
